import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X, Music4 } from "lucide-react";
import axios from "axios";
import { resolveUrl } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NUDGE_KEY = "saloni_music_nudge_dismissed_v2";

export default function AmbientPlayer() {
  const [settings, setSettings] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [nudge, setNudge] = useState(false);
  const audioRef = useRef(null);
  const fadeIntervalRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Auto-play on gate unlock (user gesture reaches here via CustomEvent → autoplay OK).
  // Handles three cases:
  //   1. Event fires while component is mounted (rare) — via listener
  //   2. Event already fired before mount — via sessionStorage flag set by gate
  //   3. Page refresh (already unlocked) — browser blocks autoplay, so wait for any user gesture
  useEffect(() => {
    if (!settings?.music_url) return;
    let attached = false;
    const tryPlay = () => {
      const a = audioRef.current;
      if (!a) return;
      setTimeout(() => {
        try {
          a.volume = 0;
          a.muted = false;
          const p = a.play();
          if (p && p.then) {
            p.then(() => {
              fadeTo(0.45, 1200);
              setPlaying(true);
              try { localStorage.setItem(NUDGE_KEY, "yes"); } catch {}
              try { sessionStorage.removeItem("saloni_should_autoplay"); } catch {}
              detachGesture();
            }).catch(() => {
              // browser refused → attach a one-shot gesture listener to retry
              attachGesture();
            });
          }
        } catch { attachGesture(); }
      }, 400);
    };
    const onFirstGesture = () => { detachGesture(); tryPlay(); };
    const attachGesture = () => {
      if (attached) return;
      attached = true;
      window.addEventListener("pointerdown", onFirstGesture, { once: true });
      window.addEventListener("keydown", onFirstGesture, { once: true });
      window.addEventListener("touchstart", onFirstGesture, { once: true });
      window.addEventListener("scroll", onFirstGesture, { once: true, passive: true });
    };
    const detachGesture = () => {
      attached = false;
      window.removeEventListener("pointerdown", onFirstGesture);
      window.removeEventListener("keydown", onFirstGesture);
      window.removeEventListener("touchstart", onFirstGesture);
      window.removeEventListener("scroll", onFirstGesture);
    };
    // Case 2: gate already unlocked before AmbientPlayer mounted (this session)
    try {
      if (sessionStorage.getItem("saloni_should_autoplay") === "1") tryPlay();
    } catch {}
    // Case 3: page refresh — already unlocked but no gesture flag. Attach listeners immediately.
    attachGesture();
    // Case 1: gate unlocks after mount (subsequent unlocks)
    const onUnlock = () => tryPlay();
    window.addEventListener("saloni:unlocked", onUnlock);
    return () => {
      window.removeEventListener("saloni:unlocked", onUnlock);
      detachGesture();
    };
  }, [settings]);

  // Duck ambient music when any <video> on the page starts playing.
  // Resume playback when the video pauses/ends. Uses capture-phase listeners on document
  // so it catches videos rendered anywhere in the tree (memory modal, VideoWish, etc.).
  useEffect(() => {
    if (!settings?.music_url) return;
    const wasPlayingBeforeVideo = { current: false };

    const onVideoPlay = (e) => {
      if (!(e.target instanceof HTMLVideoElement)) return;
      const a = audioRef.current;
      if (!a) return;
      if (!a.paused) {
        wasPlayingBeforeVideo.current = true;
        fadeTo(0, 400);
        setTimeout(() => { try { a.pause(); } catch {} setPlaying(false); }, 450);
      }
    };
    const onVideoStop = (e) => {
      if (!(e.target instanceof HTMLVideoElement)) return;
      if (!wasPlayingBeforeVideo.current) return;
      // If any other video is still playing, don't resume yet
      const anyPlaying = Array.from(document.querySelectorAll("video")).some((v) => !v.paused && !v.ended && v.currentTime > 0);
      if (anyPlaying) return;
      wasPlayingBeforeVideo.current = false;
      const a = audioRef.current;
      if (!a) return;
      try {
        a.volume = 0;
        const p = a.play();
        if (p && p.then) p.then(() => { fadeTo(0.45, 800); setPlaying(true); }).catch(() => {});
      } catch {}
    };

    document.addEventListener("play", onVideoPlay, true);
    document.addEventListener("pause", onVideoStop, true);
    document.addEventListener("ended", onVideoStop, true);
    return () => {
      document.removeEventListener("play", onVideoPlay, true);
      document.removeEventListener("pause", onVideoStop, true);
      document.removeEventListener("ended", onVideoStop, true);
    };
  }, [settings]);

  // Show a friendly nudge after the reveal so she knows to tap
  useEffect(() => {
    if (!settings?.music_url) return;
    if (playing) return;
    const dismissed = (() => { try { return localStorage.getItem(NUDGE_KEY) === "yes"; } catch { return false; } })();
    if (dismissed) return;
    const t = setTimeout(() => setNudge(true), 2500);
    return () => clearTimeout(t);
  }, [settings, playing]);

  if (!settings?.music_url || settings?.music_enabled === false) return null;

  const fadeTo = (target, ms = 700) => {
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    const a = audioRef.current;
    if (!a) return;
    const start = a.volume;
    const t0 = performance.now();
    fadeIntervalRef.current = setInterval(() => {
      const p = Math.min(1, (performance.now() - t0) / ms);
      a.volume = start + (target - start) * p;
      if (p >= 1) { clearInterval(fadeIntervalRef.current); fadeIntervalRef.current = null; }
    }, 30);
  };

  const play = async () => {
    const a = audioRef.current;
    if (!a) return;
    try {
      a.volume = 0;
      a.muted = false;
      await a.play();
      fadeTo(0.45, 900);
      setPlaying(true);
      setNudge(false);
      try { localStorage.setItem(NUDGE_KEY, "yes"); } catch {}
    } catch {}
  };

  const pause = () => {
    const a = audioRef.current;
    if (!a) return;
    fadeTo(0, 400);
    setTimeout(() => { try { a.pause(); } catch {} setPlaying(false); }, 450);
  };

  const toggle = () => (playing ? pause() : play());
  const dismissNudge = (e) => { e.stopPropagation(); setNudge(false); try { localStorage.setItem(NUDGE_KEY, "yes"); } catch {} };

  return (
    <>
      <audio ref={audioRef} src={resolveUrl(settings.music_url)} loop preload="auto" playsInline />

      {/* Nudge pill — floats up above the toggle */}
      <AnimatePresence>
        {nudge && !playing && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="fixed z-40 max-w-[280px]"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 5.5rem)", right: "calc(env(safe-area-inset-right, 0px) + 1rem)" }}
            data-testid="music-nudge"
          >
            <div className="relative rounded-2xl bg-white shadow-[0_20px_50px_-10px_rgba(11,25,44,0.25)] border border-[#EAF0F6] p-4 pr-8">
              <button
                onClick={dismissNudge}
                className="absolute top-2 right-2 w-6 h-6 rounded-full hover:bg-[#EAF0F6] flex items-center justify-center text-[#7B9ACC]"
                aria-label="Dismiss"
                data-testid="music-nudge-dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
              <p className="font-hand text-[#0B192C] text-xl leading-tight">
                Go shawty… it's <span className="italic">actually</span> your birthday.
              </p>
              <p className="mt-1 font-body text-xs text-[#334E68]">
                Tap the pill below. 50 Cent's on the guest list.
              </p>
              {/* tail arrow */}
              <div className="absolute -bottom-2 right-8 w-4 h-4 bg-white border-r border-b border-[#EAF0F6] rotate-45" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main pill — compact icon on mobile, full label on sm+ */}
      <motion.button
        data-testid="ambient-toggle"
        onClick={toggle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.7 }}
        whileHover={{ y: -3, scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        className="fixed z-40 group flex items-center gap-3 rounded-full bg-gradient-to-br from-[#0B192C] to-[#050914] text-[#F7F9FC] pl-2 pr-2 sm:pl-2.5 sm:pr-5 py-2 border border-white/12 shadow-[0_20px_45px_-10px_rgba(11,25,44,0.55)] overflow-hidden"
        style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)", right: "calc(env(safe-area-inset-right, 0px) + 1rem)" }}
        aria-label={playing ? "Pause music" : "Play music"}
      >
        {/* Pulsing ring when playing */}
        {playing && (
          <motion.span
            aria-hidden
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{ boxShadow: "inset 0 0 0 1px rgba(167,199,231,0.4)" }}
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        )}

        {/* Spinning disc icon */}
        <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#A7C7E7] to-[#7B9ACC] flex items-center justify-center">
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, #0B192C 0 22%, transparent 22%), repeating-conic-gradient(from 0deg, rgba(255,255,255,0.15) 0deg 3deg, transparent 3deg 12deg)",
            }}
            animate={{ rotate: playing ? 360 : 0 }}
            transition={{ duration: 8, repeat: playing ? Infinity : 0, ease: "linear" }}
          />
          <span className="relative w-2 h-2 rounded-full bg-[#F7F9FC]" />
        </div>

        {/* EQ bars + label — hidden on mobile for compact icon-only pill */}
        <div className="hidden sm:flex flex-col items-start leading-tight">
          <div className="flex items-center gap-2">
            <span className="font-body text-[10px] uppercase tracking-[0.28em] text-[#A7C7E7]">
              {playing ? "Now playing" : "Play music"}
            </span>
            {playing ? (
              <EqBars />
            ) : (
              <Play className="w-3 h-3 text-[#A7C7E7]" fill="currentColor" />
            )}
          </div>
          <span className="font-hand text-base text-[#F7F9FC] max-w-[200px] truncate">
            {settings.music_title || "It's Your Birthday"}
            {settings.music_artist ? ` — ${settings.music_artist}` : ""}
          </span>
        </div>

        {/* Pause icon when playing (subtle, right side) — desktop only */}
        {playing && (
          <span className="hidden sm:flex ml-1 w-6 h-6 rounded-full bg-white/10 items-center justify-center">
            <Pause className="w-3 h-3 text-[#A7C7E7]" fill="currentColor" />
          </span>
        )}
      </motion.button>
    </>
  );
}

function EqBars() {
  const bars = [0.6, 1, 0.75, 0.5];
  return (
    <div className="flex items-end gap-[3px] h-3" aria-hidden>
      {bars.map((peak, i) => (
        <motion.span
          key={i}
          className="w-[3px] rounded-sm bg-[#A7C7E7]"
          animate={{ scaleY: [0.35, peak, 0.4, peak * 0.85, 0.55] }}
          transition={{ duration: 0.9 + i * 0.15, repeat: Infinity, ease: "easeInOut" }}
          style={{ height: 12, transformOrigin: "bottom", display: "inline-block" }}
        />
      ))}
    </div>
  );
}
